import React, { useState, useMemo } from "react";
import { Card, Table, Badge, Button, Input, Alert } from "reactstrap";
import { motion } from "framer-motion";
import {
  FaSearch,
  FaFilter,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";

const ExcelPreview = ({ data, validationResults = [] }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filtrar y ordenar datos
  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter((row) => {
      const searchLower = searchTerm.toLowerCase();
      return Object.values(row).some(
        (value) => value && value.toString().toLowerCase().includes(searchLower)
      );
    });

    if (sortField) {
      filtered.sort((a, b) => {
        const aVal = a[sortField] || "";
        const bVal = b[sortField] || "";

        if (sortDirection === "asc") {
          return aVal.toString().localeCompare(bVal.toString());
        } else {
          return bVal.toString().localeCompare(aVal.toString());
        }
      });
    }

    return filtered;
  }, [data, searchTerm, sortField, sortDirection]);

  // Paginación
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredAndSortedData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Manejar ordenamiento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Obtener icono de ordenamiento
  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort className="text-muted" />;
    return sortDirection === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  // Obtener columnas del Excel
  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  // Estadísticas
  const stats = {
    total: data.length,
    filtered: filteredAndSortedData.length,
    usuarios: [
      ...new Set(
        data
          .map((row) => row["Usuario"] || row["usuario"] || row["Cedula"] || row["cedula"])
          .filter(Boolean)
      ),
    ].length,
    codigos: [
      ...new Set(
        data
          .map((row) => row["Code"] || row["code"] || row["Codigo"] || row["codigo"])
          .filter(Boolean)
      ),
    ].length,
  };

  // Estadísticas de validación
  const validationStats = {
    total: validationResults.length,
    valid: validationResults.filter(r => r.isValid).length,
    invalid: validationResults.filter(r => !r.isValid).length,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="border">
        <div className="card-header bg-light">
          <h6 className="mb-0 d-flex align-items-center">
            <FaFilter className="me-2" />
            Vista Previa de Datos
          </h6>
        </div>
        <div className="card-body">
          {/* Estadísticas */}
          <div className="row mb-3">
            <div className="col-md-3">
              <div className="text-center">
                <h5 className="text-primary mb-0">{stats.total}</h5>
                <small className="text-muted">Total Registros</small>
              </div>
            </div>
            <div className="col-md-3">
              <div className="text-center">
                <h5 className="text-success mb-0">{validationStats.valid}</h5>
                <small className="text-muted">Válidos</small>
              </div>
            </div>
            <div className="col-md-3">
              <div className="text-center">
                <h5 className="text-danger mb-0">{validationStats.invalid}</h5>
                <small className="text-muted">Con Errores</small>
              </div>
            </div>
            <div className="col-md-3">
              <div className="text-center">
                <h5 className="text-warning mb-0">{stats.filtered}</h5>
                <small className="text-muted">Filtrados</small>
              </div>
            </div>
          </div>

          {/* Búsqueda */}
          <div className="mb-3">
            <div className="input-group">
              <span className="input-group-text">
                <FaSearch />
              </span>
              <Input
                type="text"
                placeholder="Buscar en todos los campos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Tabla */}
          <div className="table-responsive">
            <Table hover striped>
              <thead className="table-dark">
                <tr>
                  {columns.map((column, index) => {
                    const isActive = sortField === column;
                    const ariaSort = isActive
                      ? sortDirection === "asc"
                        ? "ascending"
                        : "descending"
                      : "none";

                    return (
                      <th
                        key={index}
                        scope="col"
                        role="columnheader"
                        aria-sort={ariaSort}
                        onClick={() => handleSort(column)}
                        style={{
                          cursor: "pointer",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div className="d-inline-flex align-items-center gap-2">
                          <span>{column}</span>
                          {getSortIcon(column)}
                        </div>
                      </th>
                    );
                  })}
                  {validationResults.length > 0 && (
                    <th scope="col">Estado</th>
                  )}
                </tr>
              </thead>

              <tbody>
                {paginatedData.map((row, rowIndex) => {
                  // Obtener resultado de validación para esta fila
                  const validationResult = validationResults.find(result => 
                    result.rowNumber === startIndex + rowIndex + 2
                  );
                  
                  return (
                    <motion.tr
                      key={rowIndex}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: rowIndex * 0.05 }}
                      className={validationResult ? (validationResult.isValid ? 'table-success' : 'table-danger') : ''}
                    >
                      {columns.map((column, colIndex) => {
                        const value = row[column];
                        const isUsuario = column.toLowerCase().includes("usuario") || 
                                       column.toLowerCase().includes("cedula") ||
                                       column.toLowerCase().includes("ci");
                        const isCode = column.toLowerCase().includes("code") || 
                                     column.toLowerCase().includes("codigo");
                        const isNombre = column.toLowerCase().includes("nombre") || 
                                       column.toLowerCase().includes("apellido");

                        return (
                          <td key={colIndex}>
                            {isUsuario ? (
                              <Badge color="primary" className="font-monospace">
                                {value}
                              </Badge>
                            ) : isCode ? (
                              <Badge color="info" className="font-monospace">
                                {value}
                              </Badge>
                            ) : isNombre ? (
                              <Badge color="success">
                                {value}
                              </Badge>
                            ) : (
                              <span>{value}</span>
                            )}
                          </td>
                        );
                      })}
                      {validationResults.length > 0 && (
                        <td>
                          {validationResult ? (
                            <Badge color={validationResult.isValid ? 'success' : 'danger'}>
                              {validationResult.isValid ? 'Válido' : 'Error'}
                            </Badge>
                          ) : (
                            <Badge color="secondary">Sin validar</Badge>
                          )}
                        </td>
                      )}
                    </motion.tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                <small className="text-muted">
                  Mostrando {startIndex + 1} a{" "}
                  {Math.min(
                    startIndex + itemsPerPage,
                    filteredAndSortedData.length
                  )}{" "}
                  de {filteredAndSortedData.length} registros
                </small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  color="outline-secondary"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  Primera
                </Button>
                <Button
                  color="outline-secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Anterior
                </Button>
                
                {/* Páginas inteligentes */}
                <div className="d-flex gap-1">
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                    
                    // Ajustar si estamos cerca del final
                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }
                    
                    // Primera página si no está visible
                    if (startPage > 1) {
                      pages.push(
                        <Button
                          key={1}
                          color="outline-secondary"
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                        >
                          1
                        </Button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="ellipsis1" className="px-2 text-muted">...</span>
                        );
                      }
                    }
                    
                    // Páginas visibles
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          color={currentPage === i ? "primary" : "outline-secondary"}
                          size="sm"
                          onClick={() => setCurrentPage(i)}
                        >
                          {i}
                        </Button>
                      );
                    }
                    
                    // Última página si no está visible
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="ellipsis2" className="px-2 text-muted">...</span>
                        );
                      }
                      pages.push(
                        <Button
                          key={totalPages}
                          color="outline-secondary"
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </Button>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>
                
                <Button
                  color="outline-secondary"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Siguiente
                </Button>
                <Button
                  color="outline-secondary"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Última
                </Button>
              </div>
            </div>
          )}

          {/* Mensaje si no hay datos */}
          {filteredAndSortedData.length === 0 && (
            <Alert color="warning" className="text-center">
              <strong>No se encontraron registros</strong>
              <br />
              {searchTerm
                ? "Intenta con otros términos de búsqueda"
                : "No hay datos para mostrar"}
            </Alert>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

export default ExcelPreview;
